<script setup>
import { useScriptVimeoPlayer } from "#imports";
import { useIdle } from "@vueuse/core";
import { computed, h, nextTick, onMounted, ref, watch } from "vue";
import VoixVideoControls from "./VoixVideoControls.vue";
const props = defineProps({
  src: { type: String, required: true },
  options: { type: Object, required: true },
  width: { type: null, required: true },
  height: { type: null, required: true }
});
let player = {};
const video = ref();
const paused = ref(true);
const muted = ref(false);
const volume = ref(0);
const showControls = ref(false);
const { onLoaded } = useScriptVimeoPlayer({});
const { idle, reset } = useIdle(1e3);
const vimeoId = computed(() => {
  const regex = /vimeo\.com\/(?:video\/)?(\d+)/;
  const match = props.src.match(regex);
  return match ? Number(match[1]) : void 0;
});
const vimeoControls = computed(() => {
  return props.options.controls.mode === "vimeo" && props.options.controls.display;
});
const videoStyles = computed(() => {
  const styles = {
    aspectRatio: props.options.style.aspectRatio ? `aspect-ratio: ${props.options.style.aspectRatio}; ` : "",
    width: props.options.style.respectWidth ? `width: ${props.width}px; ` : "",
    height: props.options.style.respectHeight ? `height: ${props.height}px; ` : ""
  };
  return styles.aspectRatio + styles.width + styles.height;
});
function play() {
  paused.value = false;
  player.play();
}
function pause() {
  paused.value = true;
  player.pause();
}
function mute() {
  muted.value = true;
  player.setMuted(true);
}
function unMute() {
  muted.value = false;
  player.setMuted(false);
}
function setVolume({ value, init = false }) {
  volume.value = value;
  player.setVolume(value / 100);
  if (!init && Number(value) === 0)
    mute();
  else
    unMute();
}
function fullscreen() {
  player.requestFullscreen();
}
function setStyles() {
  const videoElement = video.value;
  if (videoElement && videoElement.firstElementChild) {
    const div = videoElement.firstElementChild;
    const iframe = div.firstElementChild;
    div.style.padding = "0";
    div.style.height = "100%";
    div.style.width = "100%";
    iframe.style.position = "relative";
  }
}
watch(idle, (value) => {
  if (value) {
    showControls.value = false;
  } else {
    showControls.value = true;
  }
});
onLoaded(async ({ Vimeo }) => {
  const VimeoPlayer = await Vimeo;
  await new Promise((resolve) => {
    if (typeof Vimeo === "undefined")
      VimeoPlayer.ready(resolve);
    else
      resolve();
  });
  player = new Vimeo.Player(video.value, {
    id: vimeoId.value,
    responsive: true,
    transparent: false,
    playsinline: true,
    controls: vimeoControls.value,
    autoplay: props.options.autoplay,
    loop: props.options.loop,
    fullscreen: props.options.fullscreen,
    pip: false,
    muted: props.options.mute
  });
  setTimeout(() => {
    setStyles();
    setVolume({ value: props.options.volume, init: true });
    if (props.options.mute)
      mute();
    if (props.options.autoplay) {
      play();
    }
  }, 1e3);
});
</script>

<template>
  <div class="relative">
    <div :style="videoStyles" v-bind="$attrs">
      <div
        ref="video"
        style="width: 100%; height: 100%"
      />
    </div>

    <slot v-if="options.controls.mode === 'voix' && options.controls.display" :play="play" :pause="pause" :paused="paused" :mute="mute" :un-mute="unMute" :muted="muted" :volume="volume" :set-volume="setVolume" :fullscreen="fullscreen" :allow-fullscreen="options.fullscreen">
      <div
        class="flex absolute top-0 left-0 h-full w-full text-white duration-200"
        :class="{ 'opacity-0': !showControls, 'opacity-100': showControls || paused }"
        @mouseenter="showControls = true;
  reset()"
        @mousemove="showControls = true;
  reset()"
        @mouseleave="showControls = false"
      >
        <VoixVideoControls :paused="paused" :play="play" :pause="pause" :mute="mute" :un-mute="unMute" :muted="muted" :volume="volume" :set-volume="setVolume" :fullscreen="fullscreen" :allow-fullscreen="options.fullscreen" :controls-position="options.controls.position" />
      </div>
    </slot>
  </div>
</template>
